import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import * as z from 'zod';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';

const FORM_SCHEMA = z
  .object({
    username: z.string().min(2).optional(),
    sentEmail: z.boolean(),
    code: z
      .string()
      .regex(/[0-9]{6}/)
      .optional(),
    password: z.string().min(8).max(99).optional(),
    confirmPassword: z.string().optional(),
  })
  .refine((data) => (!data.code ? data.username : true), {
    message: 'Required',
    path: ['username'],
  })
  .refine(
    (data) => {
      if (!data.username || !data.sentEmail) return true;
      else return !!data.code;
    },
    {
      message: 'Required',
      path: ['code'],
    }
  )
  .refine(
    (data) => {
      if (!data.code) return true;
      else return !!data.password;
    },
    {
      message: 'Required',
      path: ['password'],
    }
  )
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormInput = z.infer<typeof FORM_SCHEMA>;

const ResetPasswordPage: React.FC = () => {
  const { requestPasswordReset, resetPassword } = useAuth();

  const history = useHistory<any>();
  const location = useLocation<any>();

  const [loading, setLoading] = useState<boolean>(false);
  const [sentCode, setSentCode] = useState<boolean>(false);
  const [username, code, redirect] = useMemo(() => {
    const params = new URLSearchParams(location.search);

    const username =
      location.state?.username || params?.get('username') || undefined;
    const code = location.state?.code || params?.get('code') || undefined;
    const redirect =
      location.state?.redirect || params?.get('redirect') || undefined;

    return [username, code, redirect, params];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (redirect === 'reset') {
      toast('You need to reset your password to continue');

      const params = new URLSearchParams(location.search);
      params.delete('redirect');

      history.replace({
        pathname: location.pathname,
        search: params.toString(),
        state: {
          redirect: 'resetting',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirect]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormInput>({
    defaultValues: {
      sentEmail: false,
    },
    resolver: zodResolver(FORM_SCHEMA),
    shouldUnregister: false,
  });

  const onSubmitUsername = useCallback(
    async (usernameInput: string) => {
      try {
        const res = requestPasswordReset(usernameInput.trim() || '');

        await toast.promise(res, {
          loading: 'Updating new password...',
          success: (res) =>
            `Sent reset ${res.CodeDeliveryDetails.DeliveryMedium.toLowerCase()} to ${
              res.CodeDeliveryDetails.Destination
            }`,
          error: (err) => {
            console.error(err);
            if (err.code === 'UserNotFoundException') {
              return 'Username not found.';
            } else {
              return `${err.message}`;
            }
          },
        });

        setValue('sentEmail', true);
        setSentCode(true);

        await history.push({
          pathname: history.location.pathname,
          search: `?username=${usernameInput}`,
          state: { username: usernameInput },
        });
      } catch (err: any) {
        console.error(err);
      }
    },
    [requestPasswordReset, history, setValue]
  );

  const onSubmitReset = async (data: Partial<FormInput>) => {
    try {
      if (!data.username) throw new Error('Invalid username.');
      if (!data.code) throw new Error('Invalid code.');
      if (!data.password) throw new Error('Invalid password.');

      const res = resetPassword(data.username, data.code, data.password);

      await toast.promise(res, {
        loading: 'Updating new password...',
        success: 'Successfully updated password',
        error: (err) => `${err.message}`,
      });

      await history.replace({
        pathname: history.location.pathname,
      });

      await history.push('/login');
    } catch (err: any) {
      console.error(err);
    }
  };

  const onSubmit = async (data: FormInput) => {
    setLoading(true);
    if ((data.username || username) && (data.code || code) && data.password) {
      await onSubmitReset({
        username: data.username || username,
        code: data.code || code,
        password: data.password,
      });
    } else if (data.username) {
      await onSubmitUsername(data.username);
    }
    setLoading(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    const username =
      history.location.state?.username || params?.get('username');
    if (username) {
      setValue('username', username, { shouldValidate: false });
      setValue('sentEmail', true);
      setSentCode(true);
    }

    const code = history.location.state?.code || params?.get('code');
    if (code) {
      setValue('code', code);
    }
  }, [history, setValue]);

  return (
    <div className="flex items-start w-full h-full min-h-screen pt-20 bg-no-repeat bg-cover bg-purple login-background">
      <div className="container mx-auto md:w-108">
        <form
          className="px-4 py-4 mx-4 bg-white lg:px-8 lg:mx-0"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h1 className="mt-4 mb-8 text-5xl font-bold">
            Reset
            <br />
            Password
          </h1>

          <div className="space-y-4">
            <div>
              <h2 className="mb-1 text-base font-bold">Username</h2>
              <InputText
                disabled={sentCode || loading}
                placeholder="Your account username"
                {...register('username')}
              />
              {errors.username && (
                <p className="text-right text-red">
                  *{errors.username.message}
                </p>
              )}
            </div>

            {sentCode && (
              <>
                <div>
                  <h2 className="mb-1 text-base font-bold">
                    Confirmation Code
                  </h2>
                  <InputText
                    placeholder="012345"
                    disabled={code !== undefined || loading}
                    {...register('code', { valueAsNumber: false })}
                  />
                  {errors.code && (
                    <p className="text-right text-red">
                      *{errors.code.message}
                    </p>
                  )}
                </div>
                <div>
                  <h2 className="mb-1 text-base font-bold">New Password</h2>
                  <InputText
                    type="password"
                    placeholder="••••••"
                    disabled={loading}
                    {...register('password')}
                  />
                  {errors.password && (
                    <p className="text-right text-red">
                      *{errors.password.message}
                    </p>
                  )}
                </div>
                <div>
                  <h2 className="mb-1 text-base font-bold">Confirm Password</h2>
                  <InputText
                    type="password"
                    placeholder="••••••"
                    disabled={loading}
                    {...register('confirmPassword')}
                  />
                  {errors.confirmPassword && (
                    <p className="text-right text-red">
                      *{errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-center mt-8 mb-4">
            <ButtonMighty
              type="submit"
              loading={isSubmitting || loading}
              disabled={isSubmitting || loading}
              color="#ff1dfc"
              text={sentCode ? 'Change Password' : 'Reset Password'}
              borderColor="#00ff43"
              margin="0"
              textColor="#fff"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
